import React from "react"
import PropTypes from "prop-types"

/**
 * A helper that returns property descriptors for all own properties of an
 * object.
 */
function getOwnPropertyDescriptors(obj) {
  const descriptors = {}
  Object.getOwnPropertyNames(obj).forEach(key => {
    descriptors[key] = Object.getOwnPropertyDescriptor(obj, key)
  })
  return descriptors
}

/**
 * Validators return an oject with the following shape:
 * {
 *   value,      // reformatted string representation of value (optional)
 *   object,     // value object if not a string (required if underlying value is not a string)
 *   error,      // user-friendly validation error message
 *   ...details  // validation-specific details (optional)
 * }
 *
 */

/**
 * Validate basic string
 */
function stringValidator(value, { minLength = null, maxLength = null } = {}) {
  if (minLength !== null && value.length < minLength) {
    return { error: `Too short! At least ${minLength} characters required.` }
  } else if (maxLength !== null && value.length > maxLength) {
    return { error: `Too long! At most ${maxLength} characters required.` }
  } else {
    return {}
  }
}

/**
 * Validate email address
 */
function emailValidator(value) {
  const regEx = /^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i
  if (!regEx.test(value)) {
    return { error: 'Expecting something like "john.smith@example.com".' }
  } else {
    return {}
  }
}

/**
 * Validate integer
 */
function integerValidator(
  value,
  { minValue = null, maxValue = null, useGrouping = true } = {}
) {
  value = value.replace(/[^0-9-]/g, "")
  if (value == null) {
    return { value: null, object: null }
  } else if (value === "") {
    return { value, object: null }
  } else if (value === "-") {
    return {
      value: value,
      error: `"${value}" is not a valid number.`,
      object: null
    }
  } else {
    const object = parseInt(value, 10)
    value = object.toLocaleString([], { useGrouping })
    if (minValue !== null && object < minValue) {
      return {
        error: `Too small! Must be at least ${minValue.toLocaleString([], {
          useGrouping
        })}.`,
        value,
        object
      }
    } else if (maxValue !== null && object > maxValue) {
      return {
        error: `Too large! Must not be greater than ${maxValue.toLocaleString(
          [],
          { useGrouping }
        )}.`,
        value,
        object
      }
    } else {
      return {
        error: null,
        value,
        object
      }
    }
  }
}

/**
 * Validate decimal 3 3. 3.5 3.54
 */
function decimalValidator(value, { minValue = null, maxValue = null } = {}) {
  // To display the value in the form without "-"
  value = value.replace(/[^0-9^.]/g, "")
  if (value == null) {
    return { value: null, object: null }
  } else if (value === "") {
    return { value, object: null }
  } else {
    const object = parseFloat(value, 10)
    if (value.indexOf(".") !== value.lastIndexOf(".")) {
      let split = value.split(/\.(.+)/)
      value = `${split[0]}.${split[1].replace(".", "")}`
    }
    if (minValue !== null && object < minValue) {
      return {
        error: `Too small! Must be at least ${minValue}.`,
        value,
        object
      }
    } else if (maxValue !== null && object > maxValue) {
      return {
        error: `Too large! Must not be greater than ${maxValue}.`,
        value,
        object
      }
    } else {
      return {
        error: null,
        value,
        object
      }
    }
  }
}

/**
 * Validate "unit" (i.e. non-negative integer)
 */
function unitValidator(
  value,
  {
    minValue = null,
    maxValue = null,
    errorMessages = {},
    negative = false,
    useGrouping = true
  } = {}
) {
  value = value.replace(/[^0-9^.]/g, "")
  if (value == null) {
    return { value: null, object: null }
  } else if (value === "") {
    return { value, object: null }
  } else {
    const object = parseInt(value.replace(/,/g, ""), 10)
    value = object.toLocaleString([], { useGrouping })
    if (minValue !== null && object < minValue) {
      return {
        error:
          errorMessages["minValue"] ||
          `Too small! Must be at least ${minValue.toLocaleString([], {
            useGrouping
          })}.`,
        value,
        object
      }
    } else if (maxValue !== null && object > maxValue) {
      return {
        error:
          errorMessages["maxValue"] ||
          `Too large! Must not be greater than ${maxValue.toLocaleString([], {
            useGrouping
          })}.`,
        value,
        object
      }
    } else if (negative) {
      // edit and validate non-negative number, but submit negative number
      return {
        error: null,
        value,
        object: -1 * object
      }
    } else {
      return {
        error: null,
        value,
        object
      }
    }
  }
}

const booleanValidator = (value, { canBeIndeterminate = true }) => {
  if (canBeIndeterminate && value === "indeterminate") {
    return { value: "indeterminate", object: null, indeterminate: true }
  } else {
    const object = value === "true"
    return {
      value: object.toString(),
      checked: object,
      object: object,
      indeterminate: canBeIndeterminate
    }
  }
}

/**
 * Returns the formatted date string
 * @param {string} value - The date string in XX/XX/XXXX format
 * @returns {string} - The formatted date in MM/DD/YYYY format
 */

function dateValidator(value, { minValue = null, maxValue = null } = {}) {
  if (!value) {
    return {}
  }
  const isInISOFormat = !!value.match(/^\d{4}-\d{2}-\d{2}/)
  const isInImperialFormat = !!value.match(/^\d{2}\/\d{2}\/\d{4}/)
  const shortDateConfig = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }

  if (!isInISOFormat && !isInImperialFormat) {
    return { error: "Invalid date. It must be in MM/DD/YYYY format" }
  }
  let normalizedDate = ""
  if (isInImperialFormat) {
    normalizedDate = value
  }
  if (isInISOFormat) {
    const reversedDate = value.split("-").reverse()
    const monthPart = reversedDate.splice(1, 1)
    normalizedDate = monthPart.concat(reversedDate).join("/")
  }
  const [month, day, year] = normalizedDate.split("/")
  const parsedDate = new Date(year, month - 1, day)
  if (
    Number(parsedDate.getMonth() + 1) !== Number(month) ||
    parsedDate.getDate() !== Number(day)
  ) {
    return { error: "The given date is invalid. Please check" }
  }
  if (minValue !== null && minValue > parsedDate) {
    return {
      error: `This date must be after ${minValue.toLocaleDateString(
        "en-US",
        shortDateConfig
      )}`
    }
  }
  if (maxValue !== null && maxValue < parsedDate) {
    return {
      error: `This date must be before ${maxValue.toLocaleDateString(
        "en-US",
        shortDateConfig
      )}`
    }
  }
  const formattedValue = new Intl.DateTimeFormat("en-us", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  })
    .format(parsedDate)
    .replace(/[^ -~]/g, "") // This replace is for IE11 (Removing LTR/RTL)
  return {
    value: formattedValue,
    object: formattedValue
  }
}

/**
 * Returns the formatted date string
 * @param {string} value - The date string in XX/XX/XXXX format
 * @returns {string} - The formatted date in MM/DD/YYYY format
 */

function birthdateValidator(value) {
  if (!value) {
    return {}
  }
  const resp = dateValidator(value)
  if (!resp.error) {
    const [month, day, year] = resp.value.split("/")
    const parsedDate = new Date(year, month - 1, day)
    const today = new Date()
    if (
      parsedDate <
      new Date(today.getFullYear() - 120, today.getMonth(), today.getDate())
    ) {
      return {
        error:
          "Please double check the date, it is out of range for a SAVVI plan."
      }
    } else if (parsedDate > today) {
      return { error: "Birth dates in the future are not allowed." }
    }
  }
  return resp
}

/**
 * Basic value types
 */

const TYPES = {
  string: {
    propType: PropTypes.string,
    validator: stringValidator
  },
  email: {
    propType: PropTypes.string,
    validator: emailValidator
  },
  integer: {
    propType: PropTypes.number,
    validator: integerValidator
  },
  decimal: {
    propType: PropTypes.string,
    validator: decimalValidator
  },
  unit: {
    propType: PropTypes.number,
    validator: unitValidator
  },
  boolean: {
    propType: PropTypes.bool,
    validator: booleanValidator,
    empty: { value: "indeterminate", indeterminate: true, object: null }
  },
  date: {
    propType: PropTypes.string,
    validator: dateValidator
  },
  birthdate: {
    propType: PropTypes.string,
    validator: birthdateValidator
  }
}

/**
 * Return complete validation structure for schema describing an object
 * with potentially nested fields.
 */
export function validation(
  schema,
  values = {},
  validator = null,
  options = {}
) {
  // top-level of schema is always an object
  let data = objectValidation("", schema, values, true, true, options)
  // combined multi-field validator
  if (!data.hasError && validator != null) {
    return combinedValidation(data, validator)
  } else {
    return data
  }
}

/**
 * Return validation structure an object schema.
 */
function objectValidation(
  name,
  schema,
  values,
  isRequired,
  isToggledOn,
  options = {}
) {
  const fields = {}
  let hasError = false
  const isMuted = isToggledOn ? options.isMuted : true // always mute toggled-off descendants
  Object.keys(schema).forEach(key => {
    let data = nodeValidation(
      name ? name + "/" + key : key,
      schema[key],
      values !== null ? values[key] : null,
      {
        ...options,
        isMuted
      }
    )

    if (isToggledOn && data.hasError) {
      hasError = true
    }
    fields[key] = data
  })

  let error = null
  if (options.isMuted === false && isRequired && isToggledOn === null) {
    error =
      (schema.hasOwnProperty("errorMessages") &&
        schema.errorMessages.required) ||
      "This field is required."
    hasError = true
  }

  const handlers = options.createHandlers
    ? options.createHandlers(name, "object")
    : {}

  return { name, fields, isToggledOn, hasError, error, ...handlers }
}

/**
 * Return validation structure for a generic schema node.
 */
function nodeValidation(name, schema, value, options = {}, isToggledOn = null) {
  if (schema.type === "object") {
    const isRequired = schema.hasOwnProperty("required")
      ? schema.required
      : true
    if (isToggledOn == null) {
      isToggledOn = value != null ? Object.keys(value).length > 0 : null
    }
    if (value == null && isRequired) {
      value = schema.hasOwnProperty("defaultValue") ? schema.defaultValue : {}
      if (isToggledOn == null) {
        isToggledOn = value != null ? true : null
      }
    }
    return objectValidation(
      name,
      schema.schema,
      value || null,
      isRequired,
      isToggledOn,
      options
    )
  } else if (schema.type === "array") {
    return arrayValidation(name, schema, value, options)
  } else {
    return validateField(name, schema, value, options)
  }
}

/**
 * Return validation structure for an array schema.
 */
function arrayValidation(name, schema, values, options = {}) {
  const makeElement = (value, key) => {
    const elementName = name + "/" + key
    const handlers = options.createHandlers
      ? options.createHandlers(elementName, "element")
      : {}
    return {
      ...nodeValidation(
        elementName,
        { ...schema, type: "object" },
        value,
        {
          ...options,
          isMuted: options.isMuted !== undefined ? options.isMuted : true
        },
        true // isToggledOn
      ),
      key,
      ...handlers
    }
  }

  const array = values ? values.map(makeElement) : []

  const minLength = schema.minLength || 0
  if (array.length < minLength) {
    for (let i = array.length; i < minLength; i++) {
      array.push(makeElement({}, i))
    }
  }
  const nextKey = array.length
  const maxLength = schema.maxLength != null ? schema.maxLength : null
  const canAppend = maxLength == null || array.length < maxLength

  let hasError = array.some(element => element.hasError)

  const handlers = options.createHandlers
    ? options.createHandlers(name, "array")
    : {}

  return {
    name,
    array,
    hasError,
    minLength,
    maxLength,
    nextKey,
    canAppend,
    makeElement,
    ...handlers
  }
}

/**
 * Return validation structure for a field (leaf node) schema.
 */
function validateField(name, schema, value, options = {}) {
  const {
    type,
    defaultValue,
    required,
    submit = true,
    validator,
    ...validatorOptions
  } = schema

  const typeInfo = TYPES[type]
  const empty = typeInfo.empty || { value: "", object: null }
  const isRequired = !options.noRequired && (required == null || required)

  if (value == null) {
    if (defaultValue === undefined || defaultValue === null) {
      value = empty.value
    } else {
      value = typeof defaultValue === "function" ? defaultValue() : defaultValue
    }
  }
  // we honor options.createHandlers property descriptors
  const handlerProperties = getOwnPropertyDescriptors(
    options.createHandlers ? options.createHandlers(name) : {}
  )

  /**
   * Return validation structure for a single field in isolation.
   */
  const validation = (value, prevValidation = {}) => {
    let { error, object, ...validatorData } = { error: null, ...empty }

    if (value == null) value = empty.value
    if (typeof value !== "string") value = value.toString()

    const isEmpty = value === empty.value
    const isMuted =
      options.isMuted != null
        ? options.isMuted
        : prevValidation.isMuted != null
        ? prevValidation.isMuted
        : isEmpty

    if (isEmpty && type !== "boolean") {
      if (isRequired) {
        error =
          validatorOptions != null &&
          validatorOptions.errorMessages != null &&
          validatorOptions.errorMessages.hasOwnProperty("required")
            ? validatorOptions.errorMessages.required
            : "This field is required."
      }
    } else {
      if (typeInfo.validator != null) {
        ;({
          error,
          value = value,
          object = value,
          ...validatorData
        } = typeInfo.validator(value, validatorOptions))
      }
      // custom validation only if not empty and passes basic validation
      if (validator != null && !error) {
        ;({
          error,
          value = value,
          object = object,
          ...validatorData
        } = validator(value))
      }
    }

    if (value == null) {
      ;({ value, object, ...validatorData } = empty)
    }
    if (!error) {
      error = null
    }
    const isValid = !error
    const mutedError = isMuted ? error : null
    if (isMuted) {
      error = null
    }

    const validatorDataProperties = {}
    Object.keys(validatorData).forEach(key => {
      validatorDataProperties[key] = {
        value: validatorData[key],
        // whitelist of html input properties to be marked enumerable
        enumerable:
          { value: true, checked: true, indeterminate: false }[key] || false
      }
    })

    const field = Object.defineProperties(
      {},
      {
        // enumerable properties to be passed to html input form using
        // spread operator. ideally should be standard html input properties
        // (https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input)
        // or event handlers
        name: { value: name, enumerable: true },
        value: { value: value, enumerable: true },
        ...handlerProperties,

        // non-enumerable properties implicitly hidden from input element
        // but available explicitly
        object: { value: object, enumerable: false },
        isEmpty: { value: isEmpty, enumerable: false },
        isValid: { value: isValid, enumerable: false },
        isRequired: { value: isRequired, enumerable: false },
        isFocused: {
          value: prevValidation.isFocused || false,
          enumerable: false
        },
        error: { value: error, enumerable: false },
        hasError: { value: !!error, enumerable: false },
        isMuted: { value: isMuted, enumerable: false },
        mutedError: { value: mutedError, enumerable: false },
        validation: { value: validation, enumerable: false },
        submit: { value: submit, enumerable: false, configurable: true },
        ...validatorDataProperties
      }
    )
    return field
  }

  return validation(value)
}

/**
 * Return validation data updated with combined validator validation
 */
function combinedValidation(data, validator) {
  return updatedErrors(data, validator(data) || {})
}

/**
 * Return a copy of field object, including non-enumerable properties,
 * with specific property updates applied without altering the
 * enumerability of the property.
 */
function updatedCopy(field, updates) {
  const propertyDescriptors = getOwnPropertyDescriptors(field)
  Object.keys(updates).forEach(key => {
    propertyDescriptors[key].value = updates[key]
  })
  const newField = Object.create(
    Object.getPrototypeOf(field),
    propertyDescriptors
  )
  return newField
}

/**
 * Return a new validation structure updated by the specified updateAction, using
 * deep copying to avoid mutating the original data and structural sharing where
 * there are no changes to an object or its children. This is a bit like a Redux
 * reducer.
 *
 * See <https://facebook.github.io/react/docs/update.html> for related concept.
 */
export function updatedValidation(
  data,
  name,
  updateAction,
  value = null,
  validator = null
) {
  const nextData = updatedObject(data, name, updateAction, value)
  if (validator && updateAction === "set") {
    return combinedValidation(nextData, validator)
  } else {
    return nextData
  }
}

/**
 * Return object validation structure updated by the specified updateAction.
 */
function updatedObject({ fields, ...rest }, name, updateAction, value) {
  const nextData = { fields: {}, ...rest, hasError: false }
  Object.keys(fields).forEach(key => {
    const field = fields[key]
    let nextField = field
    if (name === field.name) {
      nextField = updatedField(field, updateAction, value)
    } else if (name.startsWith(field.name)) {
      if (field.hasOwnProperty("fields")) {
        nextField = updatedObject(field, name, updateAction, value)
      } else if (field.hasOwnProperty("array")) {
        const array = []
        let hasError = field.hasError
        field.array.forEach(element => {
          let nextElement = element
          if (name.startsWith(nextElement.name)) {
            nextElement = updatedObject(element, name, updateAction, value)
            if (nextElement.hasError) {
              hasError = true
            }
          }
          array.push(nextElement)
        })
        nextField = {
          ...field,
          hasError, // bubble hasError
          array
        }
      } else {
        // intentionally ignore partial match
      }
    }
    nextData.fields[key] = nextField
    if (nextField.hasError) {
      nextData.hasError = true
    }
  })
  return nextData
}

/**
 * Return field validation structure updated by the specified updateAction.
 */
function updatedField(field, updateAction, value) {
  let nextField = {}
  switch (updateAction) {
    case "set":
      return field.validation(value, field)
    case "setError":
      if (field.isMuted) {
        return updatedCopy(field, { mutedError: value, isValid: false })
      } else {
        return updatedCopy(field, { error: value, hasError: value != null })
      }
    case "focus":
      if (!field.isFocused) {
        return updatedCopy(field, { isFocused: true })
      } else {
        return field
      }
    case "blur":
      if (field.isMuted) {
        return updatedCopy(field, {
          isFocused: false,
          error: field.mutedError || null,
          hasError: field.mutedError != null,
          mutedError: null,
          isMuted: false
        })
      } else {
        return updatedCopy(field, { isFocused: false })
      }
    case "toggle":
      return {
        ...field,
        isEmpty: false, // mark that user as interacted with toggle
        isToggledOn: value != null ? value : !field.isToggledOn,
        error: null, // once toggled, can't have "unanswered" error
        hasError: Object.keys(field.fields).some(
          key => field.fields[key].hasError
        )
      }
    case "remove":
      nextField = {
        ...field,
        array: field.array.filter(element => element.key !== value)
      }
      if (nextField.array.length < nextField.minLength) {
        // ensure at least minLength elements in array
        for (let i = nextField.array.length; i < nextField.minLength; i++) {
          nextField.array.push(nextField.makeElement({}, nextField.nextKey))
          nextField.nextKey += 1
        }
      }
      return nextField
    case "append":
      // only append if it won't exceed maxLength limit
      if (field.maxLength == null || field.array.length < field.maxLength) {
        // we maintain structural sharing by not reusing keys
        return {
          ...field,
          array: [
            ...field.array,
            field.makeElement(value || {}, field.nextKey)
          ],
          nextKey: field.nextKey + 1
        }
      } else {
        return field
      }
    case "clear":
      // only clear if it won't break minLength limit
      if (field.minLength == null || field.minLength === 0) {
        // we maintain structural sharing by not reusing keys
        return {
          ...field,
          array: [],
          nextKey: field.nextKey + 1
        }
      } else {
        return field
      }
    default:
      return field
  }
}

/**
 * Return data updated with new errors
 */
function updatedErrors(data, { message, fields, ...errorInfo }) {
  if (fields) {
    let nextData = data
    Object.keys(fields).forEach(name => {
      nextData = updatedObject(nextData, name, "setError", fields[name])
    })
    return {
      ...nextData,
      error: message || null,
      hasError: Object.keys(fields).some(key => fields[key] != null),
      errorInfo
    }
  } else if (message || Object.keys(errorInfo).length) {
    return {
      ...data,
      error: message || null,
      hasError: true,
      errorInfo
    }
  } else {
    return data
  }
}

/**
 * Return underlying values from validation structure.
 */
export function validatedValues(
  data,
  all = false,
  objects = true,
  submitOnly = true
) {
  function collected(data) {
    if (data.hasOwnProperty("fields")) {
      if (data.isToggledOn) {
        const values = {}
        let fields = Object.keys(data.fields)
        if (submitOnly) {
          fields = fields.filter(field => data.fields[field].submit !== false)
        }
        fields.forEach(key => {
          const value = collected(data.fields[key])
          if (all || value != null) {
            values[key] = value
          }
        })
        return values
      } else {
        // indeterminate is null but explicitly toggled off is {}
        return data.isToggledOn != null ? {} : null
      }
    } else if (data.hasOwnProperty("array")) {
      return data.array.map(value => collected(value))
    } else {
      if (objects && data.hasOwnProperty("object")) {
        return data.object
      } else {
        return data.value
      }
    }
  }
  return collected(data)
}

/**
 * FormContainer is a higher-order component for creating self-validating
 * pure/stateless form components.
 *
 * Use it like: FormContainer(schema[, validator])(FormComponent)
 *
 * where validator is an optional multifield validator and FormComponent
 * takes the props:
 *
 *   data: { fields, hasError, onSubmit, ...etc },
 *   ...formProps
 *
 * The component passes an annotated field structure to FormComponent with
 * information about the validation state of the current input values. The
 * data is initially created by the validation function, then subsequently
 * immutably updated with the updatedValidation function.
 *
 */
export default function FormContainer(schema, validator = null) {
  return function(Form) {
    return class FormComponent extends React.Component {
      static propTypes = {
        values: PropTypes.object,
        noRequired: PropTypes.bool,
        // allowInvalid: PropTypes.bool,
        submit: PropTypes.func,
        success: PropTypes.node
      }

      static defaultProps = {
        values: {},
        noRequired: false,
        // allowInvalid: false,
        submit: values => null,
        success: null,
        forceModified: false
      }

      constructor(props) {
        super(props)
        const { values, forceModified } = props
        this.state = this.validatedState(
          values,
          undefined,
          undefined,
          forceModified
        )
        this.resetState = { ...this.state }
        this.undoIndex = null
        this.history = []
        this.timeout = null
      }

      componentDidMount() {
        if (this.props.defaultValues) {
          const keys = Object.keys(this.props.defaultValues)
          keys.forEach(key => {
            this.updateState(key, "set", this.props.defaultValues[key])
          })
        }
      }

      validatedState(
        values,
        isMuted = null,
        prevState = {},
        forceModified = false
      ) {
        const state = validation(schema, values, validator, {
          isMuted: isMuted,
          noRequired: this.props.noRequired || false,
          // allowInvalid: this.props.allowInvalid || false,
          createHandlers: this.createHandlers
        })
        state.onSubmit = this.onSubmit
        state.isModified = prevState.isModified || forceModified || false
        state.isPending = prevState.isPending || false
        return state
      }

      updateState = (
        name,
        updateAction,
        value = updateAction === "set" ? "" : null,
        record = false
      ) => {
        this.setState((prevState, props) => {
          const nextState = updatedValidation(
            prevState,
            name,
            updateAction,
            value,
            validator
          )
          if (updateAction === "set") {
            nextState.isModified = true
          } else if (updateAction === "append") {
            nextState.isModified = true
          } else if (updateAction === "remove") {
            nextState.isModified = true
          } else if (updateAction === "toggle") {
            nextState.isModified = true
          } else if (updateAction === "focus") {
            nextState.focusedFieldName = name
          } else if (updateAction === "blur") {
            nextState.focusedFieldName = null
          }
          if (record && updateAction === "blur") {
            this.undoIndex = this.history.length
            this.history.push(nextState)
          }
          return nextState
        })
      }

      undo = () => {
        if (this.history.length) {
          this.setState(this.history[this.undoIndex])
          this.undoIndex -= 1
        }
      }

      redo = () => {
        if (
          this.history.length &&
          this.undoIndex + 1 < this.history.length - 1
        ) {
          this.undoIndex += 1
          this.setState(this.history[this.undoIndex])
        }
      }

      reset = () => {
        this.setState(this.resetState)
      }

      createHandlers = (name, kind) => {
        switch (kind) {
          case "object":
            return {
              toggle: (toggleOn = null) =>
                this.updateState(name, "toggle", toggleOn)
            }
          case "array":
            return {
              append: value => this.updateState(name, "append", value),
              clear: () => this.updateState(name, "clear")
            }
          case "element":
            return {
              remove: () => {
                const arrayName = name
                  .split("/")
                  .slice(0, -1)
                  .join("/")
                const elementKey = parseInt(name.split("/").pop(), 10)
                this.updateState(arrayName, "remove", elementKey)
              },
              move: null
            }
          default:
            return {
              onFocus: event => {
                this.updateState(name, "focus")
              },
              onChange: (event, data) => {
                let value = null
                if (data) {
                  // react-semantic-ui specific handling
                  if (data.type && data.type === "checkbox") {
                    if (data.toggle) {
                      value = data.checked
                    } else {
                      value = data.value
                    }
                  } else {
                    value = data.value
                  }
                } else {
                  value = event.target.value
                }
                this.updateState(name, "set", value)
              },
              onBlur: event => {
                this.updateState(name, "blur")
              }
            }
        }
      }

      values = (objects = true, all = false, submitOnly = true) =>
        validatedValues(this.state, all, objects, submitOnly)

      onSubmit = event => {
        event.preventDefault()
        const values = this.values(false)
        const validatedState = this.validatedState(values, false, this.state)
        if (validatedState.hasError) {
          this.setState(validatedState)
        } else {
          this.resetState = { ...this.state }
          if (!this.state.isModified) {
            this.setState({ hasSucceeded: true })
          } else {
            this.setState({ isPending: true })
            return Promise.resolve(
              this.props.submit(this.values(undefined, true))
            ).then(
              result => {
                // fulfilled
                if (!!this.props.success) {
                  // only update if will be rendering success component, otherwise
                  // assume the component has already or will soon be unmounted
                  // in response to successful submit
                  validatedState.isPending = false
                  validatedState.isModified = false
                  validatedState.hasSucceeded = true
                  // return a promise wrapping asynchronous setState
                  return new Promise((resolve, reject) => {
                    this.setState(validatedState, resolve)
                  })
                }
              },
              error => {
                if (error.message === "Network Error") {
                  error = {
                    message:
                      "Network Error: Please check that you are connected to the internet and try again."
                  }
                }
                // rejected
                validatedState.isPending = false
                validatedState.isModified = true
                validatedState.hasSucceeded = false
                this.setState((prevState, props) => {
                  return updatedErrors(validatedState, error)
                })
              }
            )
          }
        }
      }

      render() {
        const {
          values,
          noRequired,
          allowInvalid,
          submit,
          success,
          forceModified,
          ...formProps
        } = this.props
        if (this.state.hasSucceeded) {
          return this.props.success
        } else {
          const { fields, ...form } = this.state
          return <Form data={{ fields, ...form }} {...formProps} />
        }
      }
    }
  }
}
